import type { SelectBoxDisplayItem } from "../selectBox.types";

export interface MultiDnDContextParams {
  search: string;
  filter: string;
  sourceItems: SelectBoxDisplayItem[];
  destinationItems: SelectBoxDisplayItem[];
  // Omitted by SelectBox when no search/filter is active, to skip cloning it into the worker.
  sourceFilteredItems?: SelectBoxDisplayItem[];
}

export interface OnMultiDnDParams {
  src: string;
  dst: string;
  contextParams: MultiDnDContextParams;
  multiSelectList: SelectBoxDisplayItem[];
  // Moved items are prepended to the destination unless set.
  appendToDestination?: boolean;
}

export interface MultiDnDResult {
  sourceItems: SelectBoxDisplayItem[];
  src: string;
  destinationItems: SelectBoxDisplayItem[];
  dst: string;
}

export type MultiDnDWorkerMessage = OnMultiDnDParams & { msg: "onMultiDnD" };

export function onMultiDnD({
  src,
  dst,
  contextParams,
  multiSelectList,
  appendToDestination,
}: OnMultiDnDParams): MultiDnDResult {
  const { search, filter, sourceItems, destinationItems, sourceFilteredItems } =
    contextParams;
  const filtered = Boolean(search) || filter !== "all";
  const sourceIndexList: number[] = [];

  multiSelectList.forEach((multiSelectItem) => {
    if (multiSelectItem.source_id === src) {
      const itemList =
        filtered && sourceFilteredItems ? sourceFilteredItems : sourceItems;
      const index = itemList.findIndex(
        (content) => multiSelectItem.uuid === content.uuid,
      );
      if (index !== -1) {
        // Mutates the shared item so callers see it moved (relied upon by SelectBox)
        // eslint-disable-next-line no-param-reassign
        multiSelectItem.source_id = dst;
        sourceIndexList.push(index);
      }
    }
  });

  const indexToRemoveList: number[] = [];
  const itemToAddList: SelectBoxDisplayItem[] = [];
  sourceIndexList.forEach((sourceIndex) => {
    let index = sourceIndex;
    let item = sourceItems[index];

    if (filtered && sourceFilteredItems) {
      const filteredItem = sourceFilteredItems[index];
      if (!filteredItem) {
        return;
      }
      item = filteredItem;
      const foundIndex = sourceItems.findIndex(
        (content) => content.uuid === filteredItem.uuid,
      );
      index = foundIndex !== -1 ? foundIndex : index;
    }

    if (!item) {
      return;
    }

    indexToRemoveList.push(index);
    itemToAddList.push(item);
  });

  const newSourceItems = sourceItems.filter(
    (_sourceItem, itemIndex) => !indexToRemoveList.includes(itemIndex),
  );
  const newDestinationItems = appendToDestination
    ? destinationItems.concat(itemToAddList)
    : itemToAddList.concat(destinationItems);

  return {
    sourceItems: newSourceItems,
    src,
    destinationItems: newDestinationItems,
    dst,
  };
}

// Stringified via worker.toString() into a Blob (see workerBuilder.ts): must stay free of injected helpers and outside references.
export default function multiDnDWorker(): void {
  // eslint-disable-next-line no-restricted-globals
  self.onmessage = (
    message: MessageEvent<MultiDnDWorkerMessage | null | undefined>,
  ) => {
    const { data } = message;
    if (!data) {
      return;
    }

    const {
      src,
      dst,
      msg,
      contextParams,
      multiSelectList,
      appendToDestination,
    } = data;

    // Make sure no one else sent unrelated events
    if (msg !== "onMultiDnD") {
      return;
    }

    const {
      search,
      filter,
      sourceItems,
      destinationItems,
      sourceFilteredItems,
    } = contextParams;

    // Kept inline (not calling onMultiDnD): this closure is stringified into the Worker blob and must stay free of outside references.
    const multiReorder = (): MultiDnDResult => {
      const filtered = Boolean(search) || filter !== "all";
      const sourceIndexList: number[] = [];
      multiSelectList.forEach((multiSelectItem) => {
        if (multiSelectItem.source_id === src) {
          const itemList =
            filtered && sourceFilteredItems ? sourceFilteredItems : sourceItems;
          const index = itemList.findIndex(
            (content) => multiSelectItem.uuid === content.uuid,
          );
          if (index !== -1) {
            // Mutates the shared item so callers see it moved (relied upon by SelectBox)
            // eslint-disable-next-line no-param-reassign
            multiSelectItem.source_id = dst;
            sourceIndexList.push(index);
          }
        }
      });

      const indexToRemoveList: number[] = [];
      const itemToAddList: SelectBoxDisplayItem[] = [];
      sourceIndexList.forEach((sourceIndex) => {
        let index = sourceIndex;
        let item = sourceItems[index];

        if (filtered && sourceFilteredItems) {
          const filteredItem = sourceFilteredItems[index];
          if (!filteredItem) {
            return;
          }
          item = filteredItem;
          const foundIndex = sourceItems.findIndex(
            (content) => content.uuid === filteredItem.uuid,
          );
          index = foundIndex !== -1 ? foundIndex : index;
        }

        if (!item) {
          return;
        }

        indexToRemoveList.push(index);
        itemToAddList.push(item);
      });

      const newSourceItems = sourceItems.filter(
        (_sourceItem, itemIndex) => !indexToRemoveList.includes(itemIndex),
      );
      const newDestinationItems = appendToDestination
        ? destinationItems.concat(itemToAddList)
        : itemToAddList.concat(destinationItems);

      return {
        sourceItems: newSourceItems,
        src,
        destinationItems: newDestinationItems,
        dst,
      };
    };

    postMessage(multiReorder());
  };
}
